import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Pagination,
  Button,
  Stack,
} from "@mui/material";
import CreateTicketDialog from './components/CUDComponents'
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { get_tickets_url, ticket_dropdown_url, project_dropdown_url, user_dropdown_url } from "./api_registry";
import AsyncSearchDropdown from "./components/AsyncSearchDropdown";
import TicketCard from "./components/TicketCard";


export default function TicketListPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Filters
  const [ticket, setTicket] = useState(null);
  const [project, setProject] = useState(null);
  const [assignedBy, setAssignedBy] = useState(null);
  const [assignedTo, setAssignedTo] = useState(null);

  // recompute ticket API url when project changes
  const ticketApiUrl = project
    ? `${ticket_dropdown_url}?project_id=${project.id}`
    : ticket_dropdown_url;

  // clear selected ticket when project filter changes
  useEffect(() => {
    setTicket(null);
  }, [project]);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 9;
  const [totalPages, setTotalPages] = useState(1);

  async function fetchTickets(pageOverride) {
    setLoading(true);

    const filter = {};
    // original API fields
    if (ticket) filter.id = ticket.id;
    if (project) filter.project_f_key = project.id;
    if (assignedBy) filter.assigned_by = assignedBy.id;
    if (assignedTo) filter.assigned_to = assignedTo.id;

    const params = new URLSearchParams({
      page: String(pageOverride ?? page),
      page_size: String(pageSize),
      filter: JSON.stringify(filter),
      sort: "created_at:desc",
    });

    const res = await fetch(`${get_tickets_url}?${params.toString()}`, {
      credentials: "include",
    });
    const data = await res.json();

    setTickets(data.items || []);
    setTotalPages(Math.ceil(data.total / pageSize));
    setLoading(false);
  }

  useEffect(() => {
    fetchTickets();
  }, [page]);

  // Auto apply filters
  useEffect(() => {
    setPage(1);
    fetchTickets(1);
  }, [ticket, project, assignedBy, assignedTo]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={3} height="100vh" display="flex" flexDirection="column">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Survey Tickets</Typography>
          <Button 
          variant="contained" 
          onClick={() => setCreateOpen(true)}
          startIcon={<AddIcon />}>Create Ticket</Button>
        </Box>

        <CreateTicketDialog open={createOpen} onClose={() => setCreateOpen(false)}/>

        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <AsyncSearchDropdown
                  label="Ticket"
                  apiUrl={ticketApiUrl}
                  value={ticket}
                  onChange={setTicket}
                  disabled={!!project && ticket?.project_id !== project.id}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AsyncSearchDropdown
                  label="Project"
                  apiUrl={project_dropdown_url}
                  value={project}
                  onChange={setProject}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AsyncSearchDropdown
                  label="Reporter"
                  apiUrl={user_dropdown_url}
                  value={assignedBy}
                  onChange={setAssignedBy}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AsyncSearchDropdown
                  label="Assignee"
                  apiUrl={user_dropdown_url}
                  value={assignedTo}
                  onChange={setAssignedTo}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Scrollable Ticket Grid */}
        <Box flex={1} overflow="auto" pr={1}>
          {loading && (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <Grid container spacing={2}>
              {tickets.map((t) => (
                <Grid key={t.id} item xs={12} sm={6} md={4}>
                  <TicketCard
                    ticket={t}
                    onEdit={() => console.log("edit", t.id)}
                    onDelete={() => console.log("delete", t.id)}
                    onView={() => console.log("view", t.id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Pagination bottom center */}
        <Stack alignItems="center" mt={2}>
          <Pagination
            page={page}
            count={totalPages}
            color="primary"
            onChange={(_, value) => setPage(value)}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
